import React from "react"
import { useTranslation } from "react-i18next"
import { useCostCenters } from "Shared/hooks/costCenter.hooks"
import { useCreditors } from "Shared/hooks/vendor.hooks"
import { useEmployees } from "Shared/hooks/employee.hooks"
import { useLoggedInEmployeeProfile } from "Shared/hooks/user.hooks"
import Form, { FormInstance } from "antd/lib/form"
import { getDynamicCostCenterList, getExpenseKindList, getDynamicVendorsList, isAdmin, isApprover, getApproverEmployeeList } from "Shared/utils/filter.utils"
import FormMinMaxDate from "./form/formMinMaxDate"
import FormSelect from "./form/formSelect"
import FormExpenseTags from "./form/formExpenseTags"
import { useCompany } from "Shared/hooks/company.hooks"
import { ExpenseFilter } from "./forms/expenseFilter.form"
import { useIsTravelEnabled } from "Shared/hooks/featureFlags.hooks"
import { Checkbox } from "antd"

interface CommonFilterFieldsInterface {
    filterData: ExpenseFilter
    formInstance: FormInstance
    rules: any
}

const CommonFilterFields: React.FC<CommonFilterFieldsInterface> = ({ filterData, formInstance, rules }) => {
    const { t } = useTranslation()
    const isTravelEnabled = useIsTravelEnabled()
    const costCenters = useCostCenters(true)
    const employees = useEmployees(true)
    const loggedInUser = useLoggedInEmployeeProfile()
    const employeeList = getApproverEmployeeList(employees)
    const expenseKindList = getExpenseKindList(isTravelEnabled)
    const { showCostCentersIds } = useCompany()

    const creditors = useCreditors(loggedInUser.activeCompanyProfile.isAdmin, true, true)

    const includeArchiveFormItem = (
        <Form.Item noStyle name="includeArchive" valuePropName="checked">
            <Checkbox className="ml-10" onClick={(domEvent) => domEvent.stopPropagation()}>
                {t("input:expense_filter.include_archived")}
            </Checkbox>
        </Form.Item>
    )
    return (
        <div>
            <FormMinMaxDate
                header={t("input:expense_filter.purchase_date_range")}
                min={{
                    name: "minDate",
                    label: t("input:expense_filter.min_date"),
                    placeholder: t("placeholder:expense_filter.min_date"),
                    initialValue: filterData.minDate,
                    rules: rules.minDate,
                    onChange: () => formInstance.validateFields(["minDate", "maxDate"]),
                }}
                max={{
                    name: "maxDate",
                    label: t("input:expense_filter.max_date"),
                    placeholder: t("placeholder:expense_filter.max_date"),
                    initialValue: filterData.maxDate,
                    rules: rules.maxDate,
                    onChange: () => formInstance.validateFields(["minDate", "maxDate"]),
                }}
                extra={includeArchiveFormItem}
            />
            <FormMinMaxDate
                header={t("input:expense_filter.invoice_date_range")}
                min={{
                    name: "minInvoiceDate",
                    label: t("input:expense_filter.min_date"),
                    placeholder: t("placeholder:expense_filter.min_date"),
                    initialValue: filterData.minInvoiceDate,
                    rules: rules.minInvoiceDate,
                    onChange: () => formInstance.validateFields(["minInvoiceDate", "maxInvoiceDate"]),
                }}
                max={{
                    name: "maxInvoiceDate",
                    label: t("input:expense_filter.max_date"),
                    placeholder: t("placeholder:expense_filter.max_date"),
                    initialValue: filterData.maxInvoiceDate,
                    rules: rules.maxInvoiceDate,
                    onChange: () => formInstance.validateFields(["minInvoiceDate", "maxInvoiceDate"]),
                }}
            />
            <FormSelect name="kind" header={t("input:expense_filter.kind")} initialValue={filterData.kind} options={expenseKindList} isCollapse fallback />
            <FormExpenseTags header={t("input:expense_filter.expenseTags")} placeholder={t("input:filter_form.not_set")} initialValue={filterData.expenseTags} />
            <FormSelect
                name="costCenter"
                header={t("input:expense_filter.cost_center")}
                initialValue={filterData.costCenter}
                options={getDynamicCostCenterList(costCenters, showCostCentersIds)}
                isCollapse
                showSearch
                fallback
            />
            {(isApprover(loggedInUser) || isAdmin(loggedInUser)) && (
                <FormSelect
                    name="requestedBy"
                    header={t("input:expense_filter.requester")}
                    initialValue={filterData.requestedBy}
                    options={employees}
                    displayImages
                    isCollapse
                    showSearch
                    fallback
                />
            )}
            <FormSelect
                name="approvalNeededBy"
                header={t("input:expense_filter.approver")}
                initialValue={filterData.approvalNeededBy}
                options={employeeList}
                displayImages
                isCollapse
                showSearch
                fallback
            />
            <FormSelect
                name="vendor"
                header={t("input:filter_form.vendor")}
                initialValue={filterData.vendor}
                options={getDynamicVendorsList(creditors as any)}
                showSearch
                isCollapse
                fallback
            />
        </div>
    )
}

export default CommonFilterFields
